import {
  UserPageData,
  UserPageDataCareerItem,
  UserPageDataRoleItem,
  UserPageDataTeamItem
} from '@maxpreps/apiserviceclients/apiModels';
import { GetUserPageDataAsync } from '@maxpreps/apiserviceclients/clients/GatewayWebServiceClient';
import { getSiteHeaderMemberData } from '@maxpreps/shared/data/wf/headers-wf';
import {
  getUserDataFromMemberCookie,
  getUserToken
} from '@maxpreps/shared/functions/utils/userUtils';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ISiteHeaderMemberData } from '../../models/wf/siteHeaderMemberData';
import {
  AsyncDataState,
  IGlobalStore,
  IMemberNameCookie,
  IPrivacyConsentStatus,
  IUserStore
} from '../reduxTypes';
import { UserStoreAction } from './userReducer';

// ghost 1

interface ICBSOptanon {
  onScriptsReady: (callback: (optanon: ICBSOptanon) => void) => void;
  ot: {
    getState: (
      callback: (
        targeting: boolean,
        performance: boolean,
        functional: boolean,
        social: boolean
      ) => void
    ) => void;
  };
}

declare global {
  interface Window {
    cbsoptanon: ICBSOptanon;
  }
}

export const useUserStore = (): IUserStore =>
  useSelector<IGlobalStore, IUserStore>(s => s.user);

/************\
    TOKEN
\************/

const getTokenThunk = () => async (dispatch, getState) => {
  const state = getState();
  if (state.user.tokenCookieState !== AsyncDataState.NotLoaded) {
    return;
  }

  dispatch({
    type: UserStoreAction.SetTokenDataState,
    payload: AsyncDataState.Loading
  });

  const token = getUserToken();

  if (typeof token === 'string') {
    dispatch({
      type: UserStoreAction.SetTokenData,
      payload: token
    });
    dispatch({
      type: UserStoreAction.SetTokenDataState,
      payload: AsyncDataState.Loaded
    });
  } else {
    dispatch({
      type: UserStoreAction.SetTokenDataState,
      payload: AsyncDataState.LoadedWithErrors
    });
    dispatch({
      type: UserStoreAction.PushUserError,
      payload: new Error('Unable to load X-MP-UserToken cookie')
    });
  }
};

export const useToken = (): string | undefined => {
  const userStore = useUserStore();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTokenThunk());
  }, [dispatch]);

  if (userStore.tokenCookieState === AsyncDataState.Loaded)
    return userStore?.tokenCookie;
};

/*****************\
    MEMBER DATA
\*****************/

const getMemberDataThunk = () => async (dispatch, getState) => {
  const state = getState();
  if (state.user.memberCookieState !== AsyncDataState.NotLoaded) {
    return;
  }

  dispatch({
    type: UserStoreAction.SetMemberDataState,
    payload: AsyncDataState.Loading
  });

  const memberCookieData = getUserDataFromMemberCookie();

  if (typeof memberCookieData !== 'undefined') {
    dispatch({
      type: UserStoreAction.SetMemberData,
      payload: memberCookieData
    });
    dispatch({
      type: UserStoreAction.SetMemberDataState,
      payload: AsyncDataState.Loaded
    });
  } else {
    dispatch({
      type: UserStoreAction.SetMemberDataState,
      payload: AsyncDataState.LoadedWithErrors
    });
    dispatch({
      type: UserStoreAction.PushUserError,
      payload: new Error('Unable to load MemberName cookie')
    });
  }
};

export const useMemberData = (): IMemberNameCookie | undefined => {
  const userStore = useUserStore();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getMemberDataThunk());
  }, [dispatch]);

  if (userStore.memberCookieState === AsyncDataState.Loaded)
    return userStore?.memberCookie;
};

export const useIsSignedIn = (): boolean => {
  const memberData = useMemberData();
  return memberData?.isSignedIn ?? false;
};

export const useUserId = (): string | undefined => {
  const memberData = useMemberData();
  return memberData?.id;
};

/*********************************\
    SITE HEADER MEMBER DATA (WF)
\*********************************/

const getSiteHeaderMemberDataThunk = userId => async (dispatch, getState) => {
  const state = getState();
  if (state.user.siteHeaderMemberDataState !== AsyncDataState.NotLoaded) {
    return;
  }

  dispatch({
    type: UserStoreAction.SetSiteHeaderMemberDataState,
    payload: AsyncDataState.Loading
  });

  const [siteHeaderMemberData, error] = await getSiteHeaderMemberData(userId);

  if (!error) {
    dispatch({
      type: UserStoreAction.SetSiteHeaderMemberData,
      payload: siteHeaderMemberData
    });
    dispatch({
      type: UserStoreAction.SetSiteHeaderMemberDataState,
      payload: AsyncDataState.Loaded
    });
  } else {
    dispatch({
      type: UserStoreAction.SetSiteHeaderMemberDataState,
      payload: AsyncDataState.LoadedWithErrors
    });
    dispatch({
      type: UserStoreAction.PushUserError,
      payload: new Error('Unable to load SiteHeaderMemberData')
    });
  }
};

export const useSiteHeaderMemberData = ():
  | ISiteHeaderMemberData
  | undefined => {
  const userStore = useUserStore();
  const dispatch = useDispatch();
  const userId = useUserId();

  useEffect(() => {
    if (!userId) return;
    dispatch(getSiteHeaderMemberDataThunk(userId));
  }, [dispatch, userId]);

  if (userStore.siteHeaderMemberDataState === AsyncDataState.Loaded)
    return userStore?.siteHeaderMemberData;
};

/***************\
    PAGE DATA
\***************/

const getUserPageDataThunk = userId => async (dispatch, getState) => {
  const state = getState();
  if (state.user.pageDataState !== AsyncDataState.NotLoaded) {
    return;
  }

  dispatch({
    type: UserStoreAction.SetPageDataState,
    payload: AsyncDataState.Loading
  });

  const [data, error] = await GetUserPageDataAsync(userId);

  console.log('data', data);
  if (!error) {
    dispatch({
      type: UserStoreAction.SetPageData,
      payload: data
    });
    dispatch({
      type: UserStoreAction.SetPageDataState,
      payload: AsyncDataState.Loaded
    });
  } else {
    dispatch({
      type: UserStoreAction.PushUserError,
      payload: error
    });
    dispatch({
      type: UserStoreAction.SetPageDataState,
      payload: AsyncDataState.LoadedWithErrors
    });
  }
};

export const useUserPageData = (): UserPageData | undefined => {
  const userStore = useUserStore();
  const dispatch = useDispatch();
  const userId = useUserId();

  useEffect(() => {
    if (!userId) return;

    dispatch(getUserPageDataThunk(userId));
  }, [dispatch, userId]);

  if (userStore.pageDataState === AsyncDataState.Loaded) {
    return userStore?.pageData;
  }
};

export const useUserFavoriteCareers = ():
  | UserPageDataCareerItem[]
  | undefined => {
  const pageData = useUserPageData();
  return pageData?.favoriteCareers;
};

export const useUserFavoriteTeams = (): UserPageDataTeamItem[] => {
  const pageData = useUserPageData();
  return pageData?.favoriteTeams ?? [];
};

export const useUserRoles = (): UserPageDataRoleItem[] => {
  const pageData = useUserPageData();
  return pageData?.roles ?? [];
};

export const getUserPrivacyConsentThunk = () => async (dispatch, getState) => {
  if (typeof window === 'undefined') {
    return;
  }

  const cbsiOptanon = window.cbsoptanon;

  if (typeof cbsiOptanon === 'undefined') {
    return;
  }

  const state = getState();

  // the callback only needs setup once, so anything other than NotLoaded we bail on
  if (
    (state.user?.privacyConsentStatus?.status ?? AsyncDataState.Loading) !==
    AsyncDataState.NotLoaded
  ) {
    return;
  }

  dispatch({
    type: UserStoreAction.SetPrivacyContestStatus,
    payload: {
      ...state.user.privacyConsentStatus,
      status: AsyncDataState.Loading
    }
  });

  cbsiOptanon.onScriptsReady(function (optanon) {
    optanon.ot.getState((...rest) => {
      const currentState = getState();

      const newState = { ...currentState.user?.privacyConsentStatus };
      newState.status = AsyncDataState.Loaded;

      ['targeting', 'performance', 'functional', 'social'].forEach(
        (k, index) => {
          newState.state[k] = rest[index];
        }
      );

      dispatch({
        type: UserStoreAction.SetPrivacyContestStatus,
        payload: newState
      });
    });
  });
};

export const useUserPrivacyConsent = (): IPrivacyConsentStatus => {
  const userStore = useUserStore();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getUserPrivacyConsentThunk());
  }, [dispatch]);

  return userStore?.privacyConsentStatus.state;
};
